import {useContext, useEffect, useState} from 'react';
import {ActionButtonItem, borderColors, splitMenuItemsIntoSections} from '../../../ui/ActionButton';
import useFunctionKeys from '../../../hooks/useFunctionKeys';
import useGlobalOkDialog from '../../../hooks/useGlobalOkDialog';
import InfoAreaDrawer from '../../../uiControls/infoArea/InfoAreaDrawer';
import constants from '../../../constants';
import {Search} from "../../../filtering";
import {opSpecialReportListItem, TableColumn, TableSettings} from '../../../tableSettings';
import AbstractListView, {
  DataFetchResult,ExportRowRenderer,ListCustomFilterRenderer,ListOrderBy,ListRowRenderer
} from "../../common/AbstractListView";
import JobListViewContext, { IJobListViewContext, RightClickContext } from '../../common/JobListViewContext';
import { TextAlignLeftRenderer } from '../../../uiControls/SmartTableRenderers';
import {JobNames} from '../../../framework.core/JobNames';
import IfmsModal from "../../../ui/IfmsModal";
import ApplicationContext from '../../../ApplicationContext';
import useGlobalYesNoDialog from '../../../hooks/useGlobalYesNoDialog';
import { findSecurityTask } from '../../../util';
import { FmsSecTask } from '../../../fms.core/constants';
import ToolboxMenuItem from '../../../uiControls/ToolboxMenuItem';
import Menu,{ IfmsMenuItem } from '../../../ui/menu/Menu';
import { AddIconButton, AddIconButton2, DeleteIconButton, DeleteIconButton2, EditIconButton, EditIconButton2, RunQueryIconButton, ViewIconButton, ViewIconButton2 } from '../../../ui/IconButton';
import { ReportQueryListItem } from '../../../fms.data/models/ReportQueryListItem';
import ListViewShortcutMenu from '../../common/ListViewShortcutMenu';
import SpecialReportDetails from './implementation/SpecialReport';
import { FormType } from '../../../fms.enum';
import { NavigationType, openNewPage } from '../../../util/navigationHelpers';
import { useHistory } from 'react-router';

function formatData(data:Array<ReportQueryListItem>) {
  return data.map(d => ({
    ...d,
  }));
}

function useSelectedOperations() {
  const appContext = useContext(ApplicationContext);
  const jobListContext = useContext(JobListViewContext);
  const selectedElement = jobListContext.selectedElement as ReportQueryListItem;
  const history = useHistory();

  const [showGlobalOkDialog] = useGlobalOkDialog();
  const [showGlobalYesNoDialog] = useGlobalYesNoDialog();
  
  async function create() {
    jobListContext.setRowExtrasId(0);
    jobListContext.setmodalShow("openQueryNew");
  }

  async function deleteQuery() {
    if (!selectedElement) 
      return;

    const doDelete = await showGlobalYesNoDialog(
      <div>
        <h3>Do you want to Delete?</h3>
      </div>
    , false, 0, true, 'warning');
    if (doDelete) {
      const apiResult = await appContext.api.deleteJobDetail(JobNames.Query, { id: selectedElement.id });
      if (apiResult && apiResult.success) {
        jobListContext.reloadData();
      } else {
        const message = apiResult?.message ?? 'Cannot delete!';
        await showGlobalOkDialog(message);
      }
    }
  }

  async function edit() {
    if (!selectedElement) 
      return;

    jobListContext.setRowExtrasId(selectedElement.id);
    jobListContext.setmodalShow("openQueryEdit");
  }

  async function view() {
    if (!selectedElement) 
      return;

    jobListContext.setRowExtrasId(selectedElement.id);
    jobListContext.setmodalShow("openQueryView");
  }

  async function showSpecialReport() {
    if (!selectedElement) 
      return;
    
    openNewPage(history, `/opReport/customize/${selectedElement.id}`, NavigationType.newPage);
  }

  return { create, deleteQuery, edit, view, showSpecialReport };
}

function RightClickMenu({dismiss}:{dismiss: VoidFunction}) {
  const { create, deleteQuery, edit, view } = useSelectedOperations();
  const jobListContext = useContext(JobListViewContext);
  const appContext = useContext(ApplicationContext);
  const listSec = findSecurityTask(appContext, FmsSecTask.OPRptSpecial);

  const items = [
    { label: 'New', shortcut: 'F2', separator: false, hidden: !listSec?.grantAdd },
    { label: 'View', shortcut: 'F9', separator: !listSec?.grantEdit },
    { label: 'Edit', shortcut: 'F3', separator: !listSec?.grantDel, hidden: !listSec?.grantEdit },
    { label: 'Delete', shortcut: '', separator: true, hidden: !listSec?.grantDel, },
    { label: 'Search', shortcut: 'F5', separator: false },
    { label: constants.Doc_Server, shortcut: 'F8', separator: true },
  ];

  const menuItems: ActionButtonItem[] = items.map((i, index) =>({
    label: <ToolboxMenuItem name="special-report-list-sub-menu" width="190px" label={i.label} shortcut={i.shortcut} />,
    separator: i.separator,
    selected: false,
    index,
    hidden: i?.hidden ?? false,
  }));

   function onMenuItemSelected(item:IfmsMenuItem) {
      switch (item.index) {
        case 0:
          create();
          break;
        case 1:
          view();
          break;
        case 2:
          edit();
          break;
        case 3:
          deleteQuery();
          break;
        case 4:
          jobListContext.triggerTableSearch('F5');
          break;
        case 5:
          jobListContext.setInfoAreaContent(jobListContext.infoAreaContent === 'memo' ? 'none' : 'memo');;
          break;
        default:
          break;
      }
      dismiss();
    }
  
   return (
      <Menu
        variant="plain"
        specialMenu='rightClickContext'
        items={splitMenuItemsIntoSections(menuItems)}
        mode="vertical"
        onMenuItemSelected={onMenuItemSelected}
        subMenuStyle={{
          minWidth: 150,
          whiteSpace: 'nowrap',
        }}
        menuStyle={(i: number) => ({
          maxHeight: '300px',
          overflowY: 'hidden',
          padding: 0,
          borderTop: `3px solid ${borderColors[i % borderColors.length]}`,
          paddingTop: 10,
        })}
        dismiss={dismiss}
      />
    );
}

function ListCommands() {
  const appContext = useContext(ApplicationContext);

  const jobListContext = useContext(JobListViewContext);
  
  const selectedElement = jobListContext.selectedElement as ReportQueryListItem;

  const { create, edit, view, deleteQuery, showSpecialReport } = useSelectedOperations();
  
  const listSec = findSecurityTask(appContext, FmsSecTask.OPRptSpecial);

  const hasSelectedRow = !!selectedElement;
  const canBeDeleted = hasSelectedRow && listSec?.grantDel;
  const canBeAdd = listSec?.grantAdd;
  const canBeEdit = hasSelectedRow && listSec?.grantEdit;

  return (
     <div className='ifms-common-no-vertical-separator-gap-margin'>
      {/* {appContext.suntekSettings?.company?.billingsTabVersion === 2 ?  <div className={!appContext.useDarkMode ? "ifms-common-vertical-separator" : "ifms-common-vertical-separator-darkmode"}/> : <></>} */}
     {appContext.user?.isSuntekAdmin &&
        <div className='ifms-common-vertical-gap-8'>
          <AddIconButton2 name='special-report-list-add-button' disabled={!canBeAdd} onClick={create} useText iconMode/>
          <EditIconButton2 name='special-report-list-edit-button' disabled={!canBeEdit} onClick={edit} useText/>
          <ViewIconButton2 name='special-report-list-view-button' disabled={!hasSelectedRow} onClick={view} useText />
          <DeleteIconButton2 name='special-report-list-delete-button' disabled={!canBeDeleted} onClick={deleteQuery} useText/>
      </div>
      }
      {/* {appContext.suntekSettings?.company?.billingsTabVersion === 2 ? <div className={!appContext.useDarkMode ? "ifms-common-vertical-separator" : "ifms-common-vertical-separator-darkmode"} /> : <></>} */}
      <RunQueryIconButton name='special-report-list-runQuery-button' disabled={!hasSelectedRow} onClick={showSpecialReport} />
    </div>
  );
}

function ListToolbar() {
  return (
    <>
      <ListViewShortcutMenu />
    </>
  );
}

function ListViewExtras({
  hideLogHistory
}: any,
){
  useFunctionKeys({onFunction: onFunctionKey});

  const jobListContext = useContext(JobListViewContext);
  const appContext = useContext(ApplicationContext);
  const listSec = findSecurityTask(appContext, FmsSecTask.OPRptSpecial);

  const { create, edit, view } = useSelectedOperations();

  const [unprocessedFkey, setUnprocessedFkey] = useState<string|null>(null);

  useEffect(() => {
      if (!unprocessedFkey) {
        return;
      }

      doProcessFkey();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unprocessedFkey]
  );

  function doProcessFkey() {
    if (unprocessedFkey === "F2") {
      if(listSec?.grantAdd) 
        create();
    } else if (unprocessedFkey === "F3") {
      if(listSec?.grantEdit) 
        edit();
    } else if (unprocessedFkey === "F9") {
      view();
    } else if (unprocessedFkey === "F11") {
      jobListContext.reloadData();
    } else if (unprocessedFkey === "F8") {
      jobListContext.setInfoAreaContent(jobListContext.infoAreaContent === 'memo' ? 'none' : 'memo');;
    }

    setUnprocessedFkey(null);
  }

  function onFunctionKey(fkey:string) {
    if (['F2', 'F3', 'F9', 'F11', 'F8'].indexOf(fkey) >= 0) {
      setUnprocessedFkey(fkey);
      return true;
    }

    return false;
  }

  return (
    <InfoAreaDrawer
      showContent={jobListContext.infoAreaContent}
      jobName={JobNames.Query}
      jobId={(jobListContext.selectedElement as ReportQueryListItem|null)?.id ?? 0}
      onClose={() => jobListContext.setInfoAreaContent('none')}
      hideLogHistory={hideLogHistory}
    />
  );
}

/**
 * Concrete implementation for Special Report List screen
 *
 * @class SpecialReportListView
 * @extends {AbstractListView}
 */
class SpecialReportListView extends AbstractListView {
  async fetchAndFormatData(api: any, search: Search, pageNum: number, limit: number): Promise<DataFetchResult> {
    const apiResult = await api.getListItems(
      JobNames.Query,
      'ReportQueryListItem',
      ReportQueryListItem.calculateSearchParameters(search),
      pageNum,
      limit,
      search.pageSize,
      search.pagePerBatch
    );

    if (!apiResult || !apiResult.entities || apiResult.entities.length <= 0) {
      return { data: [], totalCount: 0 };
    }

    return { data: formatData(apiResult.entities), totalCount: apiResult.totalCount };
  }

  async exportFormatDataWithExcel(api: any, search: Search, pageNum: number, limit: number, pageSize: number, pagePerBatch: number): Promise<Array<any>> {
    const apiResult = await api.getListItems(
      JobNames.Query,
      'ReportQueryListItem',
      ReportQueryListItem.calculateSearchParameters(search),
      pageNum,
      limit,
      pageSize,
      pagePerBatch
    );

    if (!apiResult || !apiResult.entities || apiResult.entities.length <= 0) {
      return [];
    }

    return formatData(apiResult.entities);
  }

  getCustomFilterRenderers(): ListCustomFilterRenderer[] {
    return [];
  }

  getInfoAreaEntityId() {
    return constants.infoAreaEntity.oceanImportMbl;
  }

  getRowRenderers(): ListRowRenderer[] {
    return ReportQueryListItem.getExportCollection().map(x => {
      const colRenderer:ListRowRenderer = { accessor: x.dataIndex, Cell: TextAlignLeftRenderer };
      return colRenderer;
    });
  }
  
   getExportRenderers(): ExportRowRenderer[] {
     return [];
   }

  getDefaultOrderBy(): ListOrderBy {
    return {
      orderBy: 'id',
      orderByAscending: false,
    };
  }

  getTableSettings(): TableSettings {
    const tsettings = opSpecialReportListItem;
    const cols = ReportQueryListItem.getExportCollection();
    tsettings.columns = cols.map<TableColumn>(x => {
      return { ...x, accessor: x.dataIndex, Header: x.text, filterType: undefined, filterTypeV2: x.type};
    });
    return tsettings;
  }

  renderCommands() {
    return (
      <ListCommands />
    );
  }

  renderBeforeConfigureButton() {
    return (
      <ListToolbar/>
    );
  }

  renderExtras(hideLogHistory: boolean) {
    return <ListViewExtras hideLogHistory={hideLogHistory}/>
  }

  getViewUrlFor(row: ReportQueryListItem) {
    return '';
  }

  getListViewTitle() {
    return 'Special Report List';
  };

  
  renderRowExtras(rowId: number | null, context: IJobListViewContext, cell: any,  appContext: any): React.ReactNode|null {
    if (rowId === null) {
      return null;
    }

    function onClose() {
      context.setRowExtrasId(null);
      context.setmodalShow(undefined);
    }

    if (context.modalShow == 'openQueryNew')
      return (
        <IfmsModal isBlueHeader cancelOnEsc onCancel={onClose} draggable isOpen title="NEW - Special Report" width="870px">
          <SpecialReportDetails
            selectedElementId={rowId}
            onPopupClose={onClose}
            mode={FormType.ModeAdd}
          />
        </IfmsModal>
      );
    else if (context.modalShow == 'openQueryEdit')
      return (
        <IfmsModal isBlueHeader cancelOnEsc onCancel={onClose} draggable isOpen title="EDIT - Special Report" width="870px">
          <SpecialReportDetails
            selectedElementId={rowId}
            onPopupClose={onClose}
            mode={FormType.ModeEdit}
          />
        </IfmsModal>
      );
    else if (context.modalShow == 'openQueryView')
      return (
        <IfmsModal isBlueHeader cancelOnEsc onCancel={onClose} draggable isOpen title="VIEW - Special Report" width="870px">
          <SpecialReportDetails
            selectedElementId={rowId}
            onPopupClose={onClose}
            mode={FormType.ModeView}
          />
        </IfmsModal>
      );
    else if (context.modalShow == 'openRunQuery')
      return ;
  }

  hasRightClickContext(): boolean {
    return true;
  }

  renderRightClickContext(context: RightClickContext, dismiss: VoidFunction): React.ReactNode {
    return <RightClickMenu dismiss={dismiss} />;
  }

  useLogHistory(): boolean {
    return true;
  }
};

export default SpecialReportListView;
